import { CourseDetailSkeleton } from '@/components/CourseDetailSkeleton';
import { CoursesCard } from '@/components/CoursesCard';
import { ViewHeading } from '@/components/typography/ViewHeading';
import { COURSE_TYPE } from '@/constants';
import { useAppQuery } from '@/hooks/useAppQuery';
import { CheckoutBody, useCheckoutCourse } from '@/hooks/useCheckoutCourse';
import { useGetTrainingCourseDetail } from '@/hooks/useGetTrainingCourseDetail';
import { useRequestCourseEnrolment } from '@/hooks/useRequestCourseEnrolment';
import { useSnackbar } from '@/providers/SnackbarProvider';
import CertificateIcon from '@/resources/images/ic-certificate.svg';
import CourseIcon from '@/resources/images/icons-course.svg';
import DiplomaIcon from '@/resources/images/icons-diploma.svg';
import { KLAYO_COLORS } from '@/themes';
import { ShopifyCourseDetailsResponse } from '@/types/views/courses.types';
import { track } from '@/common/track';
import { Box, Chip, Grid, Stack, Tab, Tabs, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { CourseEvent, Theme } from 'klayowebshared';
import OlcLogo from 'klayowebshared/resources/olc-logo.svg';
import { isEmpty } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button } from '../../components/Button';
import { CourseModulesTab } from '../../components/CourseModulesTab';
import { EnrolmentsTab } from '../../components/EnrolmentsTab';
import { Paper } from '../../components/Paper';
import { ScrollToTop } from '../../components/ScrollToTop';
import { View } from '../../components/View';
import { useLocale } from '../../providers/LocaleProvider';
import BackIcon from '../../resources/images/icons-arrow-left.svg';
import { PurchaseOptionsDialog } from './PurchaseOptionsDialog';

type Props = {
  theme: typeof Theme;
  title?: string;
  isTrainingManager?: boolean;
};

export const CourseDetails = (props: Props) => {
  const { theme, title, isTrainingManager } = props;
  const navigate = useNavigate();
  const location = useLocation();
  const { translate } = useLocale();
  const [showPurchaseDialog, setShowPurchaseDialog] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  const params = useParams();
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  // Which enrolment the learner opened, when they came from a My Courses card. A course taken more than
  // once has one card per LearnUpon enrolment, and the server describes that attempt rather than the
  // standing state. Absent on every other route in (search, similar courses, a skill), which keeps
  // today's behaviour.
  const [searchParams] = useSearchParams();
  const selectedEnrolmentId = searchParams.get('enrolment');
  const selectedInstance = searchParams.get('instance');

  const { data: shopifyData, isPending: isShopifyLoading } =
    useAppQuery<ShopifyCourseDetailsResponse>(`/Course/${params.id}`, {
      enabled: !isTrainingManager,
      // No keepPreviousData: navigating between two attempts of the same course changes only the query
      // params, and showing the PREVIOUS attempt's status/score while the refetch runs is exactly the
      // wrong-attempt display this parameter exists to prevent.
      placeholderData: undefined,
      config: {
        params: {
          learnUponEnrollmentId: selectedEnrolmentId ?? undefined,
          enrollmentInstance: selectedInstance ?? undefined,
        },
      },
    });

  const { data: trainingData, isPending: isTrainingLoading } = useGetTrainingCourseDetail(
    isTrainingManager ? params.id : undefined
  );

  const { onCheckoutCourse, isPending } = useCheckoutCourse(params.id);
  const { onRequestEnrolment, isPending: isRequestingEnrolment } = useRequestCourseEnrolment();

  const isLoading = isTrainingManager ? isTrainingLoading : isShopifyLoading;

  const { course, similarCourses } = useMemo(() => {
    return {
      course: isTrainingManager ? trainingData?.course : shopifyData?.course,
      similarCourses: isTrainingManager
        ? trainingData?.similarCourses
        : shopifyData?.similarCourses,
    };
  }, [isTrainingManager, shopifyData, trainingData]);

  const courseContentSections = useMemo(() => {
    return (course?.courseContentSections ?? []).map((str) => {
      const root = JSON.parse(str);
      const children: any[] = root.children ?? [];

      const headingNodes = children.filter((c) => c.type === 'heading');
      const contentNodes = children.filter((c) => c.type !== 'heading');

      return {
        title: JSON.stringify({ type: 'root', children: headingNodes }),
        content: JSON.stringify({ type: 'root', children: contentNodes }),
      };
    });
  }, [isTrainingManager, shopifyData, trainingData]);

  useEffect(() => {
    if (!course) return;
    track(CourseEvent.Viewed, {
      courseName: course.title,
      faculty: course.faculties?.map((f) => f.name),
      buyOptionsAvailable: !course.enrollmentStatus && course.isSellable !== false,
      enrolledOptionsAvailable: !!course.enrollmentStatus,
    });
  }, [course]);

  const handleBack = () => {
    // navigate(-1) is the natural Back and works for every entry point (My Learning, catalogue,
    // similar courses) without looping. ONLY the LearnUpon enrolment round-trip needs the explicit
    // origin: its previous history entries are the iframe's (polluted), so navigate(-1) would get
    // stuck. The Return flow marks that single case with `fromEnrolment`.
    const state = location.state as { from?: string; fromEnrolment?: boolean } | null;
    if (state?.fromEnrolment && state.from) navigate(state.from);
    else navigate(-1);
  };

  const handlePurchaseClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (isTrainingManager) {
      track(CourseEvent.ClickEnrolStudents, { courseName: course?.title });
      navigate(`/settings/learning/enroll-students?courseId=${params.id}`);
    } else {
      track(CourseEvent.ClickBuy, { courseName: course?.title });
      setShowPurchaseDialog(true);
    }
  };

  const handlePurchaseDialogClose = () => {
    setShowPurchaseDialog(false);
  };

  const onTabChange = (value: number) => {
    setCurrentStep(value);
  };

  const handleCheckoutCourse = (body: CheckoutBody) => {
    if (!course?.id) return;
    onCheckoutCourse(body, {
      onSuccess: (res) => {
        // window.open returns null when a popup blocker eats the tab. The checkout URL exists
        // nowhere else (nothing is persisted until Shopify's orders/paid webhook), so fall back to
        // same-tab navigation rather than losing the only copy of it — without this a blocked popup
        // was a silent dead end. The Training Manager path (EnrolStudents.tsx) does the same, and
        // additionally tracks popupBlocked and closes its dialog; neither applies here, so the
        // blocked rate on this path is not yet measurable.
        //
        // Deliberately no 'noopener': window.open returns null when it is set, which would make the
        // blocked-popup check below fire on every successful open and navigate this tab as well.
        const checkoutTab = window.open(res.data.checkoutUrl, '_blank');
        if (!checkoutTab) window.location.assign(res.data.checkoutUrl);
      },
    });
  };

  const handleRequestEnrolment = () => {
    if (!params.id) return;
    track(CourseEvent.ClickRequestEnrolment, { courseName: course?.title });
    onRequestEnrolment(params.id, {
      // Own-LMS courses are keyed by their IntegrationCourse id, not a Ceventas course id.
      isIntegrationCourse: course?.isExternalCatalogCourse,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [`/Course/${params.id}`] });
        showSnackbar(translate('coursesPage.enrolmentRequested'), 'success');
      },
      onError: () => {
        showSnackbar(translate('coursesPage.enrolmentRequestFailed'), 'error');
      },
    });
  };

  const getCourseTypeIcon = (courseType: string) => {
    switch (courseType) {
      case COURSE_TYPE.DIPLOMA:
        return <DiplomaIcon width={20} height={20} color={KLAYO_COLORS.GreyGR900} />;
      case COURSE_TYPE.CERTIFICATE:
        return <CertificateIcon width={20} height={20} color={KLAYO_COLORS.GreyGR900} />;

      default:
        return <CourseIcon width={20} height={20} color={KLAYO_COLORS.GreyGR900} />;
    }
  };

  const content = (
    <>
      {!isTrainingManager && (
        <Stack
          mb={5}
          direction={'row'}
          justifyContent={'space-between'}
          alignItems={'center'}
          gap={3}
        >
          <ViewHeading
            text={title}
            sx={{
              margin: '0 !important',
            }}
          />

          <Stack
            sx={{
              width: {
                xs: '82px',
                sm: '98px',
              },
              height: {
                xs: '40px',
                sm: '48px',
              },
            }}
          >
            <OlcLogo id='olc-logo' title='Klayo' style={{ width: '100%', height: '100%' }} />
          </Stack>
        </Stack>
      )}

      <Stack direction={'row'} spacing={2} alignItems={'center'} mb={!isTrainingManager ? 5 : 0}>
        <Button
          variant='outlined'
          onClick={handleBack}
          startIcon={<BackIcon />}
          label={translate('common.back')}
        />
      </Stack>
      <Stack gap={5}>
        <Paper
          theme={theme}
          padding={{ xs: '24px', md: '40px' }}
          sx={{
            margin: {
              xs: '0 -24px',
              sm: '0',
            },
          }}
          borderFromBreakpoint='sm'
        >
          {isLoading ? (
            <CourseDetailSkeleton />
          ) : (
            <Stack gap={5}>
              <Stack gap={2}>
                {course?.courseType && (
                  <Stack width={'max-content'}>
                    <Chip
                      sx={{
                        backgroundColor: `${KLAYO_COLORS.WhiteWH900} !important`,
                        border: `1px solid ${KLAYO_COLORS.GreyGR900} !important`,

                        '& .MuiChip-label': {
                          color: KLAYO_COLORS.GreyGR900,
                          fontWeight: 500,
                          fontSize: '14px',
                        },
                      }}
                      label={course.courseType}
                      icon={getCourseTypeIcon(course.courseType)}
                    />
                  </Stack>
                )}

                <Stack gap={0.5}>
                  <Typography
                    variant='h5'
                    sx={{
                      fontWeight: 600,
                      fontSize: {
                        xs: '20px',
                        md: '24px',
                      },
                    }}
                  >
                    {course?.title}
                  </Typography>

                  {!isEmpty(course?.faculties) && (
                    <Typography variant='body1' sx={{ color: 'var(--grey-gr-500)' }}>
                      {course?.faculties?.map((faculty) => faculty.name).join(', ')}
                    </Typography>
                  )}
                </Stack>

                {/* Rating */}
                {/* <Stack direction='row' alignItems='center'>
                  <Rating
                    value={course?.averageRating}
                    max={5}
                    size='small'
                    readOnly
                    precision={0.5}
                    sx={{
                      '& .MuiRating-iconFilled': {
                        color: KLAYO_COLORS.GreyGR900,
                      },
                      '& .MuiRating-iconHover': {
                        color: KLAYO_COLORS.GreyGR900,
                      },
                      '& .MuiRating-iconEmpty': {
                        color: KLAYO_COLORS.GreyGR900,
                      },
                    }}
                  />
                </Stack> */}
              </Stack>

              {isTrainingManager && (
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                  <Tabs
                    centered={true}
                    value={currentStep}
                    onChange={(_, value) => onTabChange(value)}
                    variant='fullWidth'
                    sx={{ width: '100%' }}
                  >
                    <Tab label={translate('myKlayo.courseDetailTab')} value={0} />
                    <Tab
                      label={translate('myKlayo.enrolmentsTabCount', {
                        count: course?.enrolmentCount ?? 0,
                      })}
                      value={1}
                    />
                  </Tabs>
                </Box>
              )}

              <Box sx={{ display: currentStep === 0 ? 'block' : 'none' }}>
                <CourseModulesTab
                  courseContentSections={courseContentSections}
                  onPurchaseClick={handlePurchaseClick}
                  isTrainingManager={isTrainingManager}
                  course={course}
                  onRequestEnrolment={handleRequestEnrolment}
                  onPurchaseMySelf={(count) => {
                    track(CourseEvent.ClickBuyMyself, { courseName: course?.title });
                    handleCheckoutCourse({ quantity: count, forMyself: true });
                  }}
                  isCheckoutLoading={isPending}
                  isRequestingEnrolment={isRequestingEnrolment}
                />
              </Box>

              {isTrainingManager && (
                <Box sx={{ display: currentStep === 1 ? 'block' : 'none' }}>
                  <EnrolmentsTab
                    theme={theme}
                    courseId={params.id}
                    isTrainingManager={isTrainingManager}
                    courseName={course?.title}
                    enabled={currentStep === 1}
                  />
                </Box>
              )}
            </Stack>
          )}
        </Paper>

        {!isEmpty(similarCourses) && (
          <>
            <Typography variant='h5' fontWeight={600}>
              {translate('coursesPage.similarCourses')}
            </Typography>

            <Box>
              <Grid container spacing={2}>
                {similarCourses?.map((item, index) => (
                  <Grid
                    key={index}
                    item
                    xs={12}
                    sm={6}
                    md={4}
                    xl={3}
                    // Up to 8 come back: two full rows at xl (4 per row); trim to whole rows below.
                    sx={{
                      display: {
                        xs: index < 4 ? 'block' : 'none',
                        md: index < 6 ? 'block' : 'none',
                        xl: 'block',
                      },
                    }}
                  >
                    <CoursesCard
                      item={item}
                      isTrainingManager={!!isTrainingManager}
                      onBeforeNavigate={() =>
                        track(CourseEvent.ClickedSimilarCourse, {
                          courseName: course?.title,
                          similarCourseName: item.title,
                        })
                      }
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          </>
        )}
      </Stack>

      <PurchaseOptionsDialog
        open={showPurchaseDialog}
        theme={theme}
        courseName={course?.title}
        onClose={handlePurchaseDialogClose}
        onPurchase={handleCheckoutCourse}
        isLoading={isPending}
      />
    </>
  );

  return isTrainingManager ? (
    <ScrollToTop key={params.id}>{content}</ScrollToTop>
  ) : (
    <View key={params.id} theme={theme} scrollToTopOnMount={true}>
      {content}
    </View>
  );
};
